import { useCallback, useEffect, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { Link, useNavigate, useParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';

import AdminForm from './adminForm';
import { ACTIONS, adminReducer, initialState } from './adminReducer';
import { dateNormalizer, errorNormalizer } from '../../shared/Helpers/functions';
import { CustomAlert, CustomButton, CustomCheckBox, Picker, Text } from '../../shared/uiComponents';
import { AdminContainer } from '../../components/StyledComponents';
import { AdminTypes } from '../../components/Menu';
import { PRIMARY } from '../../shared/Helpers/colors';
import AssignClinicalSupervisor from '../BCBA/assignClinicalSupervisor';
import AssignBCBA from '../ClinicalSupervisor/assignBCBA';

import { DispatchProperties, useSelector } from '../../redux/store';
import { getUser, getUserInsurances, updateUser } from '../../redux/State/userSlice';
import { assignRole } from '../../redux/State/identitySlice/userSlice';
import { getUserClients } from '../../redux/State/clientSlice/userClientSlice';
import { updateBillingCredentialed } from '../../redux/State/clientSlice/soapNoteSlice';
// import SelectBranch from "./selectBranch";

const today = new Date();
const month = today.getMonth();
const year = today.getFullYear();
const firstDayOfMonth = new Date(year, month, 1);

const ClientList = ({
	insuranceIds,
	clientIds,
	setClientIds,
}: {
	clientIds: string[];
	insuranceIds: string[];
	setClientIds: (id: string) => void;
}) => {
	const { userId } = useParams();
	const dispatch = useDispatch<DispatchProperties>();

	const clients = useSelector((state) => state.userClient.userClients);
	const [clientsPageSize, setClientsPageSize] = useState<number>(8);

	useEffect(() => {
		const page = '1';
		if (!userId) return;
		dispatch(
			getUserClients({
				userId,
				page,
				pageSize: clientsPageSize.toString(),
				insuranceIds,
			}),
		);
	}, [dispatch, userId, clientsPageSize, insuranceIds]);

	return (
		<>
			{clients.query?.length ? (
				<>
					<Text title="Client name" type="psm" fontWeight="semiBold" />
					{clients.query?.map((client, index) => (
						<div key={client.id}>
							<div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
								<CustomCheckBox
									item={{
										id: client.id,
										checked: clientIds.includes(client.id),
									}}
									onChange={() => setClientIds(client.id)}
								/>
								<Link
									to={`/clients/client/${client.id}`}
									target="_blank"
									style={{
										color: PRIMARY.LIGHT[32],
										lineHeight: '24px',
									}}
									key={index}
								>
									<Text title={`${index + 1}. ${client.fullName}`} type="psm" fontWeight="semiBold" />
								</Link>
							</div>
							<div className="marginLeft40 marginBottom16">
								{client.insurances.length
									? client.insurances.map((x) => (
											<Text title={`${x.type === 1 ? 'Primary:' : 'Secondary:'} ${x.name}`} type="psm" key={x.type} />
										))
									: '-'}
							</div>
						</div>
					))}
					{clients.hasNextPage && (
						<div style={{ justifyContent: 'center', display: 'flex' }}>
							<Text
								title="Load More"
								type="psm"
								fontWeight="semiBold"
								onClick={() => setClientsPageSize((prev) => prev + 8)}
							/>
						</div>
					)}
				</>
			) : (
				<Text title="There is no client affected by insurance updates." className="marginBottom16" type="psm" />
			)}
		</>
	);
};

const UpdateBillingDataContent = ({
	updatedInsuranceIds,
	onClose,
}: {
	onClose: () => void;
	updatedInsuranceIds: string[];
}) => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const { userId } = useParams();

	const [start, setStart] = useState<Dayjs>(dayjs(firstDayOfMonth));

	const [clientIds, setClientIds] = useState<string[]>([]);

	const startDateHandler = (value: Dayjs | null) => {
		if (!value) return;
		setStart(value);
	};

	const updateSoapNotesHandler = () => {
		if (!userId) return;
		const startDate = dateNormalizer(start);
		dispatch(updateBillingCredentialed({ userId, startDate, clientIds }))
			.then(unwrapResult)
			.then(() => toast('Success'))
			.catch(errorNormalizer)
			.finally(() => {
				navigate('/soap-notes');
				onClose();
			});
	};

	const updateClientIds = useCallback((id: string) => {
		setClientIds((prev) => {
			if (!prev.includes(id)) {
				return [...prev, id];
			}

			return prev.filter((x) => x !== id);
		});
	}, []);

	return (
		<div>
			<Text
				title="Please make sure to set correct Supervising BCBA in all client profiles"
				className="marginBottom16"
				type="psm"
			/>
			<Text
				title="Please make sure billing information is correct in all client profiles"
				className="marginBottom16"
				type="psm"
			/>
			<Picker.CustomDate label="Start Date" onChange={startDateHandler} value={start} />
			<ClientList insuranceIds={updatedInsuranceIds} setClientIds={updateClientIds} clientIds={clientIds} />
			<CustomButton title="Update" onClick={updateSoapNotesHandler} className="marginBottom16" />
		</div>
	);
};

const UpdateAdmin = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const { userId } = useParams();

	const formType = window.localStorage.getItem('formType') ?? '';
	const loading = useSelector((state) => state.user.loading);
	const loadingRoles = useSelector((state) => state.role.loading);
	// const role = useSelector((state) => state.role.role);
	// const isAdmin = role?.section.id === AdminTypes.admin;

	const saveDisabled = useSelector((state) => state.user.saveDisabled);
	const emailConfirmed = useSelector((state) => state.identityUser.accountInfo.emailConfirmed);

	const [adminInfo, setAdminInfo] = useReducer(adminReducer, initialState);
	const [open, setOpen] = useState<boolean>(false);
	const [statusOpen, setStatusOpen] = useState<boolean>(false);
	const [updatedInsuranceIds, setUpdatedInsuranceIds] = useState<string[]>([]);

	useEffect(() => {
		if (!userId) return;
		dispatch(getUser(userId))
			.then(unwrapResult)
			.then((response) => {
				const { role, authorizationType, canBeAssignedToClient, branches } = response;
				// Extract branchIds only if explicitly assigned (not auto-assigned)
				const branchIds =
					branches && branches.length > 0 && !branches[0].isAutoAssigned ? branches.map((b) => b.id) : [];
				setAdminInfo({ type: ACTIONS.setState, payload: response });
				setAdminInfo({ type: ACTIONS.setRoleId, payload: role.id });
				setAdminInfo({ type: ACTIONS.setBranchIds, payload: branchIds });

				if (!canBeAssignedToClient) {
					setAdminInfo({ type: ACTIONS.setInsurances, payload: [] });
					setAdminInfo({ type: ACTIONS.setAuthorizationType, payload: null });

					return;
				}

				setAdminInfo({
					type: ACTIONS.setAuthorizationType,
					payload: authorizationType?.id,
				});

				dispatch(getUserInsurances(userId))
					.then(unwrapResult)
					.then((responseResult) => {
						if (!responseResult) return;
						const payload = responseResult.map((x) => x.id);
						setAdminInfo({ type: ACTIONS.setInsurances, payload });
					});
			});
	}, [userId, dispatch]);

	// useEffect(() => {
	//   if (!userId) return;
	//   dispatch(getUserInsurances(userId))
	//     .then(unwrapResult)
	//     .then((response) => {
	//       if (!response) return;
	//       const payload = response.map((x) => x.id);
	//       setAdminInfo({ type: ACTIONS.setInsurances, payload });
	//     });
	// }, [userId, dispatch]);

	const updateAdminInfoHandler = () => {
		if (!userId) return;
		dispatch(
			assignRole({
				roleId: adminInfo.roleId,
				userId,
			}),
		)
			.then(unwrapResult)
			.then(() => {
				dispatch(
					updateUser({
						id: userId,
						data: {
							...adminInfo,
							firstName: adminInfo.firstName.trim(),
							lastName: adminInfo.lastName.trim(),
						},
					}),
				)
					.then(unwrapResult)
					.then((response) => {
						const { addedInsurances, removedInsurances } = response;
						toast('Success');

						if (!addedInsurances.length && !removedInsurances.length) {
							navigate(-1);
						}
						setOpen(Boolean(addedInsurances.length) || Boolean(removedInsurances.length));
						const added = addedInsurances.map((x) => x.id);
						const removed = removedInsurances.map((x) => x.id);
						setUpdatedInsuranceIds([...added, ...removed]);
					})
					.catch(errorNormalizer);
			})
			.finally(() => setOpen(false));
	};

	const accountStatusHandler = () => {
		setStatusOpen(true);
	};

	const accountStatusCloseHandler = () => {
		setStatusOpen(false);
	};

	const activationHandler = () => {
		if (!userId) return;
		const data = { ...adminInfo, isDeactivated: !adminInfo.isDeactivated };
		dispatch(updateUser({ id: userId, data }))
			.then(unwrapResult)
			.then(() => toast('Success'))
			.catch(errorNormalizer)
			.finally(() => {
				accountStatusCloseHandler();
				navigate(-1);
			});
	};

	const isUpdating = useSelector((state) => state.user.isUpdating);

	return (
		<>
			<AdminForm adminInfo={adminInfo} setAdminInfo={setAdminInfo} />
			<AdminContainer>
				{emailConfirmed && (
					<>
						<CustomButton
							title={`${adminInfo.isDeactivated ? 'Activate' : 'Deactivate'} account`}
							onClick={accountStatusHandler}
							className="marginBottom16"
							buttonType={adminInfo.isDeactivated ? 'primary' : 'outlined'}
							loading={loading || loadingRoles}
						/>
						<CustomAlert
							isBusy={isUpdating}
							open={statusOpen}
							title={`Do you want to ${adminInfo.isDeactivated ? 'Activate' : 'Deactivate'}?`}
							onClose={accountStatusCloseHandler}
							Content={() => (
								<>
									<CustomButton title="Confirm" onClick={activationHandler} className="marginBottom16" />
									<CustomButton title="Cancel" buttonType="secondaryGray" onClick={accountStatusCloseHandler} />
								</>
							)}
						/>
					</>
				)}
				{AdminTypes.bcba === AdminTypes[formType] && <AssignClinicalSupervisor />}
				{AdminTypes.cs === AdminTypes[formType] && <AssignBCBA />}
				{/* {isAdmin && <SelectBranch />} */}
				<CustomButton
					title="Save"
					onClick={updateAdminInfoHandler}
					disabled={saveDisabled}
					className="marginBottom16"
					loading={loading || loadingRoles}
				/>
			</AdminContainer>
			<CustomAlert
				title="User information change"
				open={open}
				onClose={() => setOpen(false)}
				Content={({ onClose }) => (
					<UpdateBillingDataContent updatedInsuranceIds={updatedInsuranceIds} onClose={onClose} />
				)}
			/>
		</>
	);
};

export default UpdateAdmin;
